import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import { authActions } from '@actions';
import { MODAL_Z_INDEX } from '@constants/modal';
import { setAppTopInset } from '@widgets/app-height';

export const IMPERSONATION_BANNER_HEIGHT = 40;

const MINUTE = 60_000;

export interface Impersonation {
    impersonatorName: string;
    impersonatorIsPartner?: boolean;
    endsAt: number;
}

const Bar = styled('div')({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    height: IMPERSONATION_BANNER_HEIGHT,
    boxSizing: 'border-box',
    padding: '0 20px',
    background: '#F3F7D7',
    borderBottom: '1px solid #DCE2A6',
    fontSize: 13,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    position: 'relative',
    zIndex: MODAL_Z_INDEX.base,
    '@media print': {
        display: 'none',
    },
});

const Text = styled('span')({
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    '& strong': {
        fontWeight: 500,
    },
});

const ExitButton = styled(Button)({
    flex: 'none',
    height: 28,
    padding: '0 12px',
    borderRadius: 8,
    fontSize: 13,
    textTransform: 'none',
    backgroundColor: '#272727',
    color: '#fff',
    '&:hover': {
        backgroundColor: '#000',
    },
});

const minutesLeft = (endsAt: number, now: number): number => Math.max(0, Math.ceil((endsAt - now) / MINUTE));

const ImpersonationBanner = () => {
    const impersonation = useSelector(
        (state: { auth: { impersonation: Impersonation | null } }) => state.auth.impersonation,
    );
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [now, setNow] = useState(Date.now);

    const impersonating = impersonation != null;
    useEffect(() => {
        if (!impersonating) {
            return undefined;
        }

        setAppTopInset(IMPERSONATION_BANNER_HEIGHT);
        const timer = window.setInterval(() => setNow(Date.now()), MINUTE / 2);
        return () => {
            window.clearInterval(timer);
            setAppTopInset(0);
        };
    }, [impersonating]);

    const expired = impersonation != null && impersonation.endsAt <= now;
    useEffect(() => {
        if (expired) {
            dispatch(authActions.logout());
        }
    }, [expired, dispatch]);

    if (!impersonation) {
        return null;
    }

    const exit = async () => {
        await authActions.endImpersonation();
        dispatch(authActions.logout());
        window.close();
    };

    return (
        <Bar role="status">
            <Text>
                <strong>
                    {t(
                        impersonation.impersonatorIsPartner
                            ? 'ImpersonationBanner.impersonatedByPartner'
                            : 'ImpersonationBanner.impersonatedBy',
                        { name: impersonation.impersonatorName },
                    )}
                </strong>
                {' · '}
                {t('ImpersonationBanner.minutesLeft', { count: minutesLeft(impersonation.endsAt, now) })}
            </Text>
            <ExitButton onClick={exit} disableElevation>
                {t('ImpersonationBanner.exit')}
            </ExitButton>
        </Bar>
    );
};

export default ImpersonationBanner;
